import React, { useState, useMemo } from 'react'
import { Button, Stack, TextField, Typography, Link } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import BusinessIcon from '@mui/icons-material/Business'
import RemoveConfirmDialog from 'common/components/RemoveConfirmDialog'

import { DistrictInfo } from '../types'
import CollapsibleSection from './CollapsibleSection'
import DataGridTable from './DataGridTable'
import LicenseModal from './LicenseModal'

interface DistrictSectionProps {
  district: DistrictInfo | null
  parentOrigin: string
  actionLoading: boolean
  postAction: (action: string, body?: Record<string, unknown>) => void
}

function DistrictSection({
  district,
  parentOrigin,
  actionLoading,
  postAction,
}: DistrictSectionProps) {
  const [tenantId, setTenantId] = useState('')
  const [oCode, setOCode] = useState('')
  const [addDistrictOpen, setAddDistrictOpen] = useState(false)
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false)

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Code',
        width: 160,
        renderCell: (params) => (
          <Link
            href={`${parentOrigin}/district?o=${params.value}`}
            target='_blank'
            underline='hover'
          >
            {params.value}
          </Link>
        ),
      },
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'oneRosterId', headerName: 'Tenant ID', width: 200 },
    ],
    [parentOrigin]
  )

  const rows = district ? [{ id: district.code, ...district }] : []

  const handleAdd = () => {
    postAction('addDistrict', { tenantId, oCode })
    setTenantId('')
    setOCode('')
    setAddDistrictOpen(false)
  }

  const handleRemove = () => {
    if (!district) return
    postAction('removeDistrict', { oCode: district.code })
  }

  return (
    <>
      <CollapsibleSection
        title='District'
        count={district ? 1 : 0}
        icon={BusinessIcon}
        onAdd={!district ? () => setAddDistrictOpen(true) : undefined}
        onDeleteSelected={
          district ? () => setRemoveConfirmOpen(true) : undefined
        }
        deleteDisabled={actionLoading}
        deleteCount={district ? 1 : 0}
      >
        {district ? (
          <DataGridTable
            rows={rows}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
          />
        ) : (
          <Typography variant='body2' color='text.secondary'>
            There are no districts.
          </Typography>
        )}
      </CollapsibleSection>

      <LicenseModal
        open={addDistrictOpen}
        onClose={() => setAddDistrictOpen(false)}
        title='Add District'
        primaryLabel='Add District'
        onPrimary={handleAdd}
        primaryDisabled={actionLoading || (!tenantId && !oCode)}
      >
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <TextField
            size='small'
            label='ClassLink Tenant ID'
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            fullWidth
          />
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ textAlign: 'center' }}
          >
            — or —
          </Typography>
          <TextField
            size='small'
            label='District Code (oCode)'
            value={oCode}
            onChange={(e) => setOCode(e.target.value)}
            fullWidth
          />
        </Stack>
      </LicenseModal>

      <RemoveConfirmDialog
        open={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
        onConfirm={handleRemove}
        title='Remove District'
        disabled={actionLoading}
      />
    </>
  )
}

export default React.memo(DistrictSection)
